import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ArrowRight, ArrowLeft, Check, Sparkles, Award, Target, MessageSquareText,
  Wand2, Trophy, GraduationCap, ClipboardList, Timer, Star, Layers,
  RefreshCw,
} from "lucide-react";

const useReveal = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const n = ref.current; if (!n) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setInView(true); return; }
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.unobserve(n); } }, { threshold: 0.15, rootMargin: "0px 0px -60px 0px" });
    obs.observe(n); return () => obs.disconnect();
  }, []);
  return { ref, inView };
};

/* ============ HERO ============ */
const FloatCard = ({ Icon, title, pos, delay, inView, tint = "text-[#1d4ed8]" }) => (
  <div className={`absolute ${pos} z-10 medily-float-slow`} style={{
    transform: inView ? "translate3d(0,0,0) scale(1)" : "translate3d(0,20px,0) scale(0.9)",
    opacity: inView ? 1 : 0,
    transition: `transform 900ms cubic-bezier(0.22,1,0.36,1) ${delay}ms, opacity 700ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
  }}>
    <div className="flex items-center gap-2.5 rounded-2xl bg-white border border-white px-3.5 py-2.5 shadow-[0_25px_55px_-15px_rgba(29,78,216,0.4)] backdrop-blur">
      <span className={`w-8 h-8 rounded-lg bg-[#eef4ff] flex items-center justify-center ${tint}`}><Icon className="w-4 h-4" /></span>
      <span className="text-[12.5px] font-semibold text-slate-800 whitespace-nowrap">{title}</span>
    </div>
  </div>
);

const HeroVisual = ({ inView }) => (
  <div className="relative w-full" style={{ perspective: "1200px" }}>
    <div className="absolute -inset-8 -z-10 rounded-[52px] bg-gradient-to-br from-[#c9dcff] via-[#dbe6ff] to-transparent blur-3xl opacity-95" />
    <div className="absolute inset-x-8 -bottom-4 -z-10 h-10 rounded-full bg-[#1d4ed8]/30 blur-2xl" />
    <img src="/homepage/Practise 1 L.png" alt="Maya on laptop"
      style={{
        transform: inView ? "translate3d(0,0,0) rotate(0) scale(1)" : "translate3d(-60px,24px,0) rotate(-2deg) scale(0.97)",
        opacity: inView ? 1 : 0,
        transition: "transform 1100ms cubic-bezier(0.22,1,0.36,1) 120ms, opacity 900ms cubic-bezier(0.22,1,0.36,1) 120ms",
      }}
      className="w-full h-auto drop-shadow-[0_50px_80px_rgba(29,78,216,0.28)] select-none" draggable="false" data-testid="maya-hero-laptop" />
    <img src="/homepage/Practise 1 P.png" alt="Maya on phone"
      style={{
        transform: inView ? "translate3d(0,0,0) rotate(0) scale(1)" : "translate3d(-40px,32px,0) rotate(3deg) scale(0.94)",
        opacity: inView ? 1 : 0,
        transition: "transform 1300ms cubic-bezier(0.22,1,0.36,1) 320ms, opacity 1000ms cubic-bezier(0.22,1,0.36,1) 320ms",
      }}
      className="absolute -bottom-[2%] left-[-3%] md:left-[-6%] w-[36%] md:w-[32%] h-auto drop-shadow-[0_40px_75px_rgba(29,78,216,0.45)] select-none" draggable="false" data-testid="maya-hero-phone" />
    <FloatCard Icon={GraduationCap} title="University-Specific" pos="top-4 -right-4 md:-right-8" delay={480} inView={inView} />
    <FloatCard Icon={MessageSquareText} title="Feedback Ready" pos="top-1/2 -right-3 md:-right-10" delay={620} inView={inView} tint="text-[#15803d]" />
    <FloatCard Icon={Wand2} title="Optimised Answer" pos="bottom-16 -right-4 md:-right-6" delay={760} inView={inView} />
    <FloatCard Icon={Trophy} title="MMI Score 8.4/10" pos="bottom-2 -left-4 md:-left-6" delay={900} inView={inView} tint="text-amber-500" />
  </div>
);

const Hero = () => {
  const { ref, inView } = useReveal();
  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="maya-hero-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-40 -left-40 w-[720px] h-[720px] bg-[#bcd3ff] rounded-full blur-3xl opacity-90" />
        <div className="absolute top-24 -right-40 w-[640px] h-[640px] bg-[#d5e4ff] rounded-full blur-3xl opacity-95" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.65),transparent_60%)]" />
      </div>
      <div className="max-w-7xl mx-auto px-6 pt-20 md:pt-24 pb-28 md:pb-36 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
        <div style={{
          transform: inView ? "translate3d(0,0,0)" : "translate3d(0,28px,0)",
          opacity: inView ? 1 : 0,
          transition: "transform 900ms cubic-bezier(0.22,1,0.36,1), opacity 700ms cubic-bezier(0.22,1,0.36,1)",
        }}>
          <span className="inline-block px-3 py-1 rounded-full bg-white/70 backdrop-blur border border-[#dbe6ff] text-[#1d4ed8] text-[12px] font-semibold tracking-[0.14em] uppercase shadow-sm">Maya</span>
          <h1 className="mt-6 font-semibold text-slate-900 leading-[1.02] tracking-tight text-[46px] md:text-[64px]" data-testid="maya-hero-heading">
            Practise with confidence.{" "}
            <span className="relative inline-block whitespace-nowrap">
              <span className="relative z-10 text-[#1d4ed8]">Improve with Maya</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
              <span className="relative z-10">.</span>
            </span>
          </h1>
          <p className="mt-6 text-slate-500 text-[17px] leading-relaxed max-w-[580px]">
            Maya is your personalised MMI interview coach, designed specifically for UK medical school
            interviews. Practise realistic stations, receive{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#1d4ed8] font-medium">university-specific</span>
              <span className="absolute left-0 right-0 bottom-0.5 h-2.5 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
            </span>{" "}
            feedback and see how your answer could be improved.
          </p>
        </div>
        <div><HeroVisual inView={inView} /></div>
      </div>
    </section>
  );
};

/* ============ HOW MAYA WORKS ============ */
const STEPS = [
  { n: "01", icon: Target, title: "Personalise your interview", body: "Choose your target university, station themes, number of stations and difficulty.", featured: false },
  { n: "02", icon: ClipboardList, title: "Answer the question", body: "Complete a realistic MMI station by typing your response within the available time.", featured: false },
  { n: "03", icon: MessageSquareText, title: "Receive detailed feedback", body: "University-specific feedback: What Went Well, Even Better If, category scores and overall station score.", featured: true },
  { n: "04", icon: Wand2, title: "Review your optimised answer", body: "Maya rewrites your response to demonstrate what a stronger top-mark answer could look like.", featured: true },
];

const StepCard = ({ step, i }) => {
  const { ref, inView } = useReveal();
  const Icon = step.icon;
  return (
    <div ref={ref} style={{
      transform: inView ? "translate3d(0,0,0)" : "translate3d(0,24px,0)",
      opacity: inView ? 1 : 0,
      transition: `transform 900ms cubic-bezier(0.22,1,0.36,1) ${i * 110}ms, opacity 700ms cubic-bezier(0.22,1,0.36,1) ${i * 110}ms`,
    }} className={`relative rounded-3xl p-7 md:p-8 border transition-all duration-500 hover:-translate-y-1 ${
      step.featured
        ? "bg-gradient-to-br from-[#1d4ed8] via-[#2555df] to-[#3b6ff5] text-white border-transparent shadow-[0_40px_90px_-25px_rgba(29,78,216,0.7)] hover:shadow-[0_50px_110px_-25px_rgba(29,78,216,0.8)]"
        : "bg-white text-slate-900 border-slate-100 shadow-[0_25px_60px_-25px_rgba(29,78,216,0.35)] hover:shadow-[0_35px_75px_-25px_rgba(29,78,216,0.5)]"
    }`} data-testid={`maya-step-${i}`}>
      {step.featured && (
        <span className="absolute -top-3 right-6 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white text-[#1d4ed8] text-[10px] font-bold tracking-wider uppercase shadow-md">
          <Sparkles className="w-2.5 h-2.5" /> Maya only
        </span>
      )}
      <div className="flex items-center justify-between">
        <span className={`text-[11px] font-bold tracking-[0.24em] ${step.featured ? "text-white/70" : "text-[#1d4ed8]"}`}>STEP {step.n}</span>
        <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${step.featured ? "bg-white/15 text-white" : "bg-[#eaf1ff] text-[#1d4ed8]"}`}><Icon className="w-5 h-5" /></span>
      </div>
      <h3 className={`mt-5 text-[19px] font-semibold tracking-tight leading-tight ${step.featured ? "text-white" : "text-slate-900"}`}>{step.title}</h3>
      <p className={`mt-2 text-[13.5px] leading-relaxed ${step.featured ? "text-white/85" : "text-slate-600"}`}>{step.body}</p>
    </div>
  );
};

const HowMayaWorks = () => (
  <section className="max-w-7xl mx-auto px-6 pt-24 md:pt-28 pb-8" data-testid="maya-steps-section">
    <div className="text-center max-w-3xl mx-auto">
      <span className="inline-block px-3 py-1 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[12px] font-medium">How it works</span>
      <h2 className="mt-4 text-[34px] md:text-[46px] font-semibold text-slate-900 tracking-tight leading-[1.05]">
        Your Maya interview in{" "}
        <span className="relative inline-block whitespace-nowrap">
          <span className="relative z-10 text-[#1d4ed8]">four simple steps</span>
          <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
        </span>.
      </h2>
    </div>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {STEPS.map((s, i) => <StepCard key={s.n} step={s} i={i} />)}
    </div>
  </section>
);

/* ============ INTERACTIVE PREVIEW ============ */
const SetupScreen = () => (
  <div className="p-6 md:p-8 space-y-5 pointer-events-none select-none" data-testid="maya-preview-setup">
    <div className="rounded-2xl bg-gradient-to-br from-[#1d4ed8] to-[#3b6ff5] text-white p-8 md:p-10 relative overflow-hidden text-center">
      <img
        src="https://customer-assets-rejwkqb3.emergentagent.net/job_medily-pro/artifacts/do6r0b0h_Maya%20%28without%20white%20background%29.png"
        alt="Maya"
        className="mx-auto h-10 md:h-12 w-auto"
        style={{ filter: "brightness(0) invert(1)" }}
        draggable="false"
      />
      <p className="mt-4 text-[13px] font-medium uppercase tracking-[0.14em] text-white/90">Your AI MMI Interview Coach</p>
      <p className="mt-3 text-[13px] text-white/85 max-w-md mx-auto leading-relaxed">
        Configure a mock interview tailored to your target medical school. Maya adapts every station
        to the standard that university actually expects.
      </p>
      <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#1d4ed8] text-[11.5px] font-semibold">
          <span className="w-4 h-4 rounded-full bg-[#1d4ed8] text-white text-[9px] flex items-center justify-center">1</span>
          University
        </span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#1d4ed8] text-[11.5px] font-semibold">
          <span className="w-4 h-4 rounded-full bg-slate-200 text-slate-500 text-[9px] flex items-center justify-center">2</span>
          Themes
        </span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#1d4ed8] text-[11.5px] font-semibold">
          <span className="w-4 h-4 rounded-full bg-slate-200 text-slate-500 text-[9px] flex items-center justify-center">3</span>
          Format
        </span>
      </div>
    </div>
    <div className="rounded-2xl bg-white border border-slate-100 p-4">
      <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">Monthly usage</p>
      <div className="flex items-center justify-between mt-1">
        <p className="text-[14px] font-semibold text-slate-900">7 / 15 stations used this month</p>
        <p className="text-[11px] text-slate-400">8 stations remaining. Resets on the 1st.</p>
      </div>
      <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full w-[47%] bg-gradient-to-r from-[#1d4ed8] to-[#4f7cff]" /></div>
    </div>
    <div className="rounded-2xl bg-white border border-slate-100 p-5">
      <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#1d4ed8]">Step 1</p>
      <p className="mt-1 text-[15px] font-semibold text-slate-900">Choose your target university</p>
      <div className="mt-4 grid grid-cols-4 gap-2">
        {["Imperial College London","UCL","King's College London","University of Edinburgh","University of Manchester","University of Bristol","University of Leeds","University of Oxford","Cardiff University","University of Liverpool","Newcastle University","University of Dundee"].map((u,i) => (
          <div key={i} className={`text-[10.5px] text-center px-2 py-2 rounded-lg border ${i === 4 ? "border-[#1d4ed8] bg-[#eef4ff] text-[#1d4ed8] font-semibold" : "border-slate-100 text-slate-600"}`}>{u}</div>
        ))}
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="rounded-2xl bg-white border border-slate-100 p-5">
        <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#1d4ed8]">Step 3</p>
        <p className="mt-1 text-[14px] font-semibold text-slate-900">How many stations?</p>
        <div className="mt-3 flex gap-2">{[4,6,8,10].map(n => <span key={n} className={`flex-1 text-center py-2 rounded-lg text-[13px] ${n === 6 ? "bg-[#1d4ed8] text-white font-semibold" : "bg-slate-50 text-slate-500"}`}>{n}</span>)}</div>
      </div>
      <div className="rounded-2xl bg-white border border-slate-100 p-5">
        <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#1d4ed8]">Step 4</p>
        <p className="mt-1 text-[14px] font-semibold text-slate-900">Difficulty</p>
        <div className="mt-3 space-y-1.5">
          <div className="text-[12px] px-3 py-1.5 rounded-lg bg-slate-50 text-slate-500">Foundation</div>
          <div className="text-[12px] px-3 py-1.5 rounded-lg bg-[#eef4ff] text-[#1d4ed8] font-semibold border border-[#1d4ed8]/30">Standard</div>
          <div className="text-[12px] px-3 py-1.5 rounded-lg bg-slate-50 text-slate-500">Challenging</div>
        </div>
      </div>
    </div>
    <div className="rounded-2xl bg-gradient-to-r from-[#1d4ed8] to-[#3b6ff5] text-white p-4 flex items-center justify-between">
      <div>
        <p className="text-[10.5px] uppercase tracking-[0.2em] opacity-80">Summary</p>
        <p className="text-[13px] font-semibold">University of Manchester · 6 stations · Standard</p>
      </div>
      <span className="px-4 py-2 rounded-full bg-white text-[#1d4ed8] text-[12.5px] font-semibold shadow-sm">Start interview</span>
    </div>
  </div>
);

const StationScreen = () => (
  <div className="p-6 md:p-8 space-y-4 pointer-events-none select-none" data-testid="maya-preview-station">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className="text-[13px] font-bold tracking-wide text-[#1d4ed8]">Maya</span>
        <span className="text-slate-300">|</span>
        <span className="text-[13px] text-slate-600">University of Manchester</span>
      </div>
      <div className="flex items-center gap-2 text-[11px]">
        <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">Standard</span>
      </div>
    </div>
    <div className="text-[11px] flex justify-between text-slate-400">
      <span>Station 3 of 6</span>
      <span>2 completed</span>
    </div>
    <div className="rounded-2xl bg-white border border-slate-100 p-6">
      <div className="flex items-center gap-2">
        <span className="px-2.5 py-1 rounded-md bg-[#1d4ed8] text-white text-[11px] font-bold uppercase tracking-wider">Station 3</span>
        <span className="text-[13px] text-slate-500">Ethical Scenario</span>
        <span className="ml-2 px-2 py-0.5 rounded bg-[#eef4ff] text-[10px] font-bold text-[#1d4ed8] uppercase tracking-widest">Medical Ethics</span>
      </div>
      <p className="mt-4 text-[19px] text-slate-900 leading-relaxed font-medium">
        A close friend confides in you that they have been struggling with mental health and asks you not to tell anyone.
        A week later, you notice they have missed several days of class and appear increasingly withdrawn. How would you
        approach this situation and what factors would guide your decision?
      </p>
    </div>
    <div className="rounded-2xl bg-white border border-slate-100 p-5">
      <div className="flex items-center justify-between">
        <p className="text-[13px] font-semibold text-slate-900">Your answer</p>
        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><Timer className="w-3 h-3" /> 4:32</span>
          <span className="px-2 py-0.5 rounded bg-slate-50">184 / 400 words</span>
        </div>
      </div>
      <div className="mt-3 h-40 rounded-xl bg-slate-50 p-4 text-[13px] text-slate-500 leading-relaxed">
        I would balance my duty to respect my friend's confidence with my responsibility to their safety. First, I would gently
        approach my friend and…
      </div>
      <div className="mt-3 flex justify-end">
        <span className="px-5 py-2 rounded-full bg-[#1d4ed8] text-white text-[12.5px] font-semibold opacity-90">Submit answer</span>
      </div>
    </div>
  </div>
);

const FeedbackScreen = () => (
  <div className="p-6 md:p-8 space-y-4 pointer-events-none select-none" data-testid="maya-preview-feedback">
    <div className="text-[11px] flex items-center gap-2 text-[#1d4ed8] font-bold tracking-[0.2em] uppercase">
      <Sparkles className="w-3 h-3" /> Feedback for station 3 · University of Manchester
    </div>
    <div className="rounded-2xl bg-white border border-slate-100 p-6 grid grid-cols-[120px_1fr] gap-6 items-center">
      <div className="relative w-[120px] h-[120px]">
        <svg viewBox="0 0 120 120" className="w-full h-full">
          <circle cx="60" cy="60" r="52" stroke="#eef4ff" strokeWidth="12" fill="none" />
          <circle cx="60" cy="60" r="52" stroke="#1d4ed8" strokeWidth="12" fill="none" strokeDasharray="326" strokeDashoffset="65" strokeLinecap="round" transform="rotate(-90 60 60)" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-[34px] font-semibold text-slate-900 leading-none">8</span>
          <span className="text-[10px] text-slate-400">out of 10</span>
        </div>
      </div>
      <div className="space-y-2">
        {[["Communication",8.5],["Structure",7],["Knowledge",8],["Professionalism",9],["Empathy",7.5]].map(([label, val]) => (
          <div key={label}>
            <div className="flex items-center justify-between text-[11.5px]"><span className="text-slate-600">{label}</span><span className="font-semibold text-slate-800">{val}/10</span></div>
            <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#1d4ed8] to-[#4f7cff]" style={{ width: `${val*10}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
    <div className="grid md:grid-cols-2 gap-4">
      <div className="rounded-2xl border border-[#c7f0d8] bg-[#f0faf3] p-5">
        <p className="inline-flex items-center gap-2 text-[12px] font-bold text-[#15803d]"><Check className="w-3.5 h-3.5" /> STRENGTHS · MANCHESTER</p>
        <ul className="mt-3 space-y-2 text-[12.5px] text-slate-700 leading-relaxed">
          <li>• Strong reflective tone aligned with Manchester's emphasis on values-based practice.</li>
          <li>• Person-centred opening that mirrors Manchester's focus on empathy and rapport.</li>
          <li>• Clear ethical reasoning — safeguarding weighed against confidentiality without dismissing autonomy.</li>
          <li>• Structured response following a recognise → respond → escalate pathway.</li>
        </ul>
      </div>
      <div className="rounded-2xl border border-[#f6dcbc] bg-[#fdf5ec] p-5">
        <p className="inline-flex items-center gap-2 text-[12px] font-bold text-[#c2410c]"><Sparkles className="w-3.5 h-3.5" /> AREAS FOR IMPROVEMENT · MANCHESTER</p>
        <ul className="mt-3 space-y-2 text-[12.5px] text-slate-700 leading-relaxed">
          <li>• Manchester interviewers value reflection — briefly link this scenario back to your own values or experiences.</li>
          <li>• Reference specific safeguarding frameworks (Gillick, Fraser, MCA 2005) to add professional weight.</li>
          <li>• Manchester's Personal Qualities framework rewards proportionality — explain why you would escalate rather than disclose immediately.</li>
          <li>• Close with a one-sentence resolution that ties your reasoning back to patient welfare.</li>
        </ul>
      </div>
    </div>
    <div className="rounded-2xl bg-white border border-slate-100 p-6">
      <div className="flex items-center gap-2"><Wand2 className="w-4 h-4 text-[#1d4ed8]" /><p className="text-[12px] font-bold tracking-[0.2em] uppercase text-[#1d4ed8]">Optimised answer · Tailored to Manchester</p></div>
      <p className="mt-3 text-[13.5px] text-slate-700 leading-relaxed">
        This situation requires me to balance my duty to respect my friend's confidence with my
        responsibility for their welfare — a balance that sits at the heart of Manchester's focus on
        reflective, values-led practice. My first step would be to create space for a private,
        judgement-free conversation, using open questions and active listening to understand how
        they are feeling and what support they have in place. If risk indicators emerged — such as
        expressed hopelessness, worsening isolation or thoughts of self-harm — I would gently
        acknowledge that my concern for them is greater than any promise I could realistically keep.
      </p>
      <p className="mt-3 text-[13.5px] text-slate-700 leading-relaxed">
        I would proportionately signpost them to trusted sources of confidential help — their GP,
        the university wellbeing service or a crisis helpline — and reassure them that seeking
        support is not a loss of autonomy but an act of self-compassion. Where appropriate, I would
        reference safeguarding frameworks such as Gillick and Fraser and the Mental Capacity Act
        2005, particularly if there was any suggestion of impaired capacity. Finally, I would
        reflect on why Manchester values this scenario: it tests empathy, ethical reasoning and the
        courage to act in a patient's best interests — the same qualities that will define me as a
        future doctor.
      </p>
    </div>
  </div>
);

const SCREENS = [
  { id: "setup", label: "Interview Setup", Comp: SetupScreen },
  { id: "station", label: "MMI Station", Comp: StationScreen },
  { id: "feedback", label: "Feedback", Comp: FeedbackScreen },
];

const InteractivePreview = () => {
  const [idx, setIdx] = useState(0);
  const Cur = SCREENS[idx].Comp;
  return (
    <section className="max-w-7xl mx-auto px-6 pt-24 md:pt-28 pb-16" data-testid="maya-preview-section">
      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-block px-3 py-1 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[12px] font-medium">Try it yourself</span>
        <h2 className="mt-4 text-[34px] md:text-[46px] font-semibold text-slate-900 tracking-tight">See Maya in action.</h2>
        <p className="mt-4 text-slate-500 text-[16px] leading-relaxed">Explore the Maya interview journey before starting your own practice session.</p>
      </div>
      <div className="mt-12 rounded-[28px] overflow-hidden border border-slate-100 bg-[#f7faff]/50 shadow-[0_45px_110px_-30px_rgba(29,78,216,0.5)]">
        {/* Fake browser chrome */}
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-slate-100 bg-white/70 pointer-events-none select-none">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" /><span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" /><span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          <span className="ml-3 text-[11px] text-slate-400">medily.co.uk / maya / {SCREENS[idx].id}</span>
        </div>
        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 py-4 border-b border-slate-100 bg-white/50">
          {SCREENS.map((s, i) => (
            <button key={s.id} onClick={() => setIdx(i)} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-semibold transition ${i === idx ? "bg-[#1d4ed8] text-white shadow-[0_10px_25px_-10px_rgba(29,78,216,0.6)]" : "bg-white text-slate-600 border border-slate-100 hover:border-[#1d4ed8]/40"}`}
              data-testid={`maya-preview-tab-${s.id}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${i === idx ? "bg-white/20" : "bg-[#eef4ff] text-[#1d4ed8]"}`}>{i + 1}</span>
              {s.label}
            </button>
          ))}
        </div>
        <div className="bg-white min-h-[560px]" data-testid="maya-preview-body"><Cur /></div>
        {/* Nav */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-white/70">
          <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-200 text-[13px] text-slate-700 disabled:opacity-40 hover:border-[#1d4ed8]/40 transition"
            data-testid="maya-preview-prev">
            <ArrowLeft className="w-4 h-4" /> Previous
          </button>
          <span className="text-[12px] text-slate-400">{idx + 1} / {SCREENS.length}</span>
          <button onClick={() => setIdx(Math.min(SCREENS.length - 1, idx + 1))} disabled={idx === SCREENS.length - 1}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1d4ed8] text-white text-[13px] font-semibold disabled:opacity-40 hover:bg-[#1e40af] transition"
            data-testid="maya-preview-next">
            Next <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ============ FEATURES ============ */
const FEATURES = [
  { id: "stations", icon: RefreshCw, title: "15 AI MMI Stations Every Month", body: "Your subscription includes 15 Maya stations every monthly billing cycle. Your allowance resets automatically each billing date.", featured: true },
  { id: "uni", icon: GraduationCap, title: "University-Specific Practice", body: "Practise stations tailored to your chosen medical school." },
  { id: "wwei", icon: Award, title: "What Went Well & Even Better If", body: "Clearly understand your strengths and how to improve." },
  { id: "opt", icon: Wand2, title: "Optimised Answer", body: "Compare your answer with Maya's improved version." },
  { id: "progress", icon: Trophy, title: "Scores & Progress Tracking", body: "Track overall scores and monitor your improvement over time." },
];

const FeatureCard = ({ f, i }) => {
  const { ref, inView } = useReveal();
  const Icon = f.icon;
  return (
    <div ref={ref} style={{
      transform: inView ? "translate3d(0,0,0)" : "translate3d(0,22px,0)",
      opacity: inView ? 1 : 0,
      transition: `transform 900ms cubic-bezier(0.22,1,0.36,1) ${i * 90}ms, opacity 700ms cubic-bezier(0.22,1,0.36,1) ${i * 90}ms`,
    }} className={`relative rounded-3xl p-6 md:p-8 border transition-all duration-500 hover:-translate-y-1 ${
      f.featured
        ? "md:col-span-2 md:row-span-1 bg-gradient-to-br from-[#1d4ed8] via-[#2555df] to-[#3b6ff5] text-white border-transparent shadow-[0_45px_100px_-25px_rgba(29,78,216,0.7)]"
        : "bg-white text-slate-900 border-slate-100 shadow-[0_20px_50px_-25px_rgba(29,78,216,0.35)]"
    }`} data-testid={`maya-feature-${f.id}`}>
      <div className="flex items-center justify-between">
        <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${f.featured ? "bg-white/15 text-white" : "bg-[#eaf1ff] text-[#1d4ed8]"}`}><Icon className="w-5 h-5" /></span>
        {f.featured && <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/80">Included every month</span>}
      </div>
      <h4 className={`mt-5 font-semibold tracking-tight leading-tight ${f.featured ? "text-[26px] md:text-[30px]" : "text-[18px]"}`}>{f.title}</h4>
      <p className={`mt-2 leading-relaxed ${f.featured ? "text-white/85 text-[14.5px] max-w-[520px]" : "text-slate-600 text-[14px]"}`}>{f.body}</p>
      {f.featured && (
        <div className="mt-5 grid grid-cols-3 gap-2 max-w-[520px]">
          {["Choose your university", "Choose your theme", "Choose your difficulty"].map((step, i) => (
            <div key={i} className="rounded-xl bg-white/10 border border-white/15 p-3 text-center flex items-center justify-center min-h-[70px]">
              <p className="text-[11.5px] font-semibold leading-tight">{step}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Features = () => (
  <section className="max-w-7xl mx-auto px-6 pt-16 pb-24" data-testid="maya-features-section">
    <div className="text-center max-w-2xl mx-auto">
      <span className="inline-block px-3 py-1 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[12px] font-medium">What's included</span>
      <h2 className="mt-4 text-[34px] md:text-[46px] font-semibold text-slate-900 tracking-tight leading-[1.05]">
        Everything you need to{" "}
        <span className="relative inline-block whitespace-nowrap">
          <span className="relative z-10 text-[#1d4ed8]">practise with purpose</span>
          <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
        </span>.
      </h2>
    </div>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {FEATURES.map((f, i) => <FeatureCard key={f.id} f={f} i={i} />)}
    </div>
  </section>
);

/* ============ FINAL CTA ============ */
const FinalCta = () => {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="maya-final-cta-section">
      <div className="rounded-[32px] bg-gradient-to-br from-[#eef4ff] via-white to-[#dbe6ff] border border-white p-12 md:p-20 text-center shadow-[0_45px_110px_-30px_rgba(29,78,216,0.55)]">
        <h3 className="text-[34px] md:text-[52px] font-semibold text-slate-900 tracking-tight leading-[1.05]">
          Ready to start{" "}
          <span className="relative inline-block whitespace-nowrap">
            <span className="relative z-10 text-[#1d4ed8]">improving</span>
            <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
          </span>?
        </h3>
        <p className="mt-5 text-slate-600 max-w-2xl mx-auto text-[16.5px] leading-relaxed">
          Practise realistic MMI stations, receive personalised university-specific feedback and build confidence before interview day.
        </p>
        <div className="mt-9 flex justify-center">
          <button onClick={() => navigate("/pricing")} className="px-8 py-4 rounded-full bg-[#1d4ed8] text-white font-medium hover:bg-[#1e40af] transition shadow-[0_18px_45px_-12px_rgba(29,78,216,0.6)] inline-flex items-center gap-2" data-testid="maya-final-cta-btn">
            Get Started <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

const MayaLanding = () => (
  <div className="bg-white min-h-screen">
    <Navbar />
    <Hero />
    <HowMayaWorks />
    <InteractivePreview />
    <Features />
    <FinalCta />
    <Footer />
  </div>
);

export default MayaLanding;
